import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { User } from 'firebase/auth';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import { auth, db } from '../../firebase';
import { onAuthStateChanged } from 'firebase/auth';

type PermissionKey = 'users' | 'facilities' | 'facilityReservations' | 'portville' | 'portvilleRooms' | 'parking' | 'notices';

interface AdminAuthState {
  user: any | null;
  userLevel: number | null;
  permissions: {
    users: boolean;
    facilities: boolean;
    facilityReservations: boolean;
    portville: boolean;
    portvilleRooms: boolean;
    parking: boolean;
    notices: boolean;
  } | null;
  loading: boolean;
  hasPermission: (permission: PermissionKey) => boolean;
}

interface UserProfile {
  userLevel: number;
  name: string;
  email: string;
  organization: string;
}

/**
 * 관리자 권한 확인 및 인증 상태를 관리하는 커스텀 훅
 * @param redirectTo - 권한이 없을 경우 리다이렉트할 경로 (기본값: /admin/signin)
 * @returns AdminAuthState - 관리자 인증 상태 정보
 */
export const useAdminAuth = (redirectTo: string = '/admin/signin') => {
  const navigate = useNavigate();
  const [authState, setAuthState] = useState<AdminAuthState>({
    user: null,
    userLevel: null,
    permissions: null,
    loading: true,
    hasPermission: () => false
  });

  const updateLastLoginAtIfNeeded = async (userId: string) => {
    try {
      const userProfileRef = doc(db, 'user_profiles', userId);
      const userProfileSnap = await getDoc(userProfileRef);
      
      if (userProfileSnap.exists()) {
        const userData = userProfileSnap.data();
        const lastLoginAt = userData.lastLoginAt;
        const now = new Date();
        const oneHourAgo = new Date(now.getTime() - 60 * 60 * 1000);
        
        // lastLoginAt이 없거나, 1시간 이전의 기록인 경우에만 업데이트
        if (!lastLoginAt || 
            lastLoginAt === 'Invalid Date' || 
            new Date(lastLoginAt) < oneHourAgo) {
          await updateDoc(userProfileRef, {
            lastLoginAt: now.toISOString()
          });
        }
      }
    } catch (error) {
      console.error('관리자 lastLoginAt 자동 업데이트 실패:', error);
      // 업데이트 실패는 인증 플로우에 영향을 주지 않음
    }
  };

  useEffect(() => {
    let lastUserId: string | null = null;
    
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        try {
          const userDoc = await getDoc(doc(db, 'user_profiles', user.uid));
          if (userDoc.exists()) {
            const userData = userDoc.data();
            const permissions = userData.permissions || null;
            
            const hasPermission = (permission: PermissionKey): boolean => {
              if (!permissions) return false;
              return permissions[permission] || false;
            };

            // 새로운 관리자가 로그인했을 때만 lastLoginAt 업데이트
            if (user.uid !== lastUserId && (userData.userLevel === 1 || userData.userLevel === 2)) {
              lastUserId = user.uid;
              await updateLastLoginAtIfNeeded(user.uid);
            }

            setAuthState({
              user,
              userLevel: userData.userLevel || null,
              permissions,
              loading: false,
              hasPermission
            });
          } else {
            setAuthState({
              user: null,
              userLevel: null,
              permissions: null,
              loading: false,
              hasPermission: () => false
            });
          }
        } catch (error) {
          console.error('관리자 정보 조회 실패:', error);
          setAuthState({
            user: null,
            userLevel: null,
            permissions: null,
            loading: false,
            hasPermission: () => false
          });
        }
      } else {
        lastUserId = null;
        setAuthState({
          user: null,
          userLevel: null,
          permissions: null,
          loading: false,
          hasPermission: () => false
        });
      }
    });

    return () => unsubscribe();
  }, []);

  return authState;
};

export default useAdminAuth;
